import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

import { Avatar, Popover, PopoverTrigger, TopNav, UserMenu } from "@performyard/py-components";

import EmployeeSearch from "../../containers/EmployeeSearch/EmployeeSearch";

import { useFeatureFlagQuery } from "../../hooks/useFeatureFlag";

import type { NavigationConfig } from "../../api/services/defs/navigation";

import { isNoSidebarRoute } from "./layout";
import { SideNavLogo } from "./sideNavLogo";
import { SIDE_NAV_PRIMARY_BLUE_THEME } from "./theme";
import { buildUserMenuOptions } from "./userMenuOptions";

/** Hover-close debounce so the menu stays open while crossing the trigger to content gap. */
const DROPDOWN_CLOSE_DELAY_MS = 200;

type TopBarPortalProps = {
	config: NavigationConfig;
	onToggleMobileSidenav?: () => void;
	isMobileSidenavOpen?: boolean;
};

const TopBarPortal = ({ config, onToggleMobileSidenav, isMobileSidenavOpen = false }: TopBarPortalProps) => {
	const { value: isDirectoryEnabled } = useFeatureFlagQuery("ff-is-directory-enabled", false);
	const location = useLocation();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const openDropdown = () => {
		if (closeTimerRef.current) {
			clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
		setIsDropdownOpen(true);
	};
	const scheduleCloseDropdown = () => {
		if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
		closeTimerRef.current = setTimeout(() => setIsDropdownOpen(false), DROPDOWN_CLOSE_DELAY_MS);
	};
	useEffect(
		() => () => {
			if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
		},
		[]
	);

	const mount = typeof document !== "undefined" ? document.getElementById("top-bar") : null;

	const userMenuOptions = useMemo(() => buildUserMenuOptions(config), [config]);

	if (!mount) return null;

	const noSidebar = isNoSidebarRoute(location.pathname);
	const sideNavTheme = config.sideNavTheme ?? SIDE_NAV_PRIMARY_BLUE_THEME;

	const userMenu = (
		<Popover open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="cursor-pointer rounded-full border-none bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--sidenav-fg,currentColor)]"
					aria-label="User account menu"
					onMouseEnter={openDropdown}
					onMouseLeave={scheduleCloseDropdown}
				>
					<Avatar size={32} id={config.currentUserId} alt={`${config.currentUserName} profile menu`} />
				</button>
			</PopoverTrigger>
			<UserMenu
				forceMount
				side="bottom"
				align="end"
				sideOffset={8}
				options={userMenuOptions}
				onSelect={() => setIsDropdownOpen(false)}
				onMouseEnter={openDropdown}
				onMouseLeave={scheduleCloseDropdown}
				data-testid="topbar-user-menu"
				optionTestIdPrefix="topbar-dropdown"
				theme={noSidebar ? sideNavTheme : undefined}
			>
				{/* Stable mount target for JobsList; keep the id present even when the menu is closed. */}
				<div id="react-task-watcher" className="watcher-in-dropdown react-task-watcher" />
			</UserMenu>
		</Popover>
	);

	return createPortal(
		<TopNav
			logo={noSidebar ? <SideNavLogo config={config} /> : undefined}
			theme={sideNavTheme}
			searchSlot={isDirectoryEnabled ? <EmployeeSearch rootElementId={undefined} /> : undefined}
			userMenu={userMenu}
			onToggleMobileSidenav={onToggleMobileSidenav}
			isMobileSidenavOpen={isMobileSidenavOpen}
		/>,
		mount
	);
};

export default TopBarPortal;
